import { useState, useCallback, useRef } from 'react';
import { IChatMessage } from 'interfaces/components/chatbot/chatbot';
import { askAI, AskAIRequestBody } from 'network/askAI';
import { addMessage, RateLimitError } from 'utils/helpers/chatbotHelpers';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

export const useChatMessages = (
  messages: IChatMessage[],
  setMessages: React.Dispatch<React.SetStateAction<IChatMessage[]>>
) => {
  const [isLoading, setIsLoading] = useState(false);
  const email = useSelector((state: RootState) => state.companyInfo.email);
  const lastResponseIdRef = useRef<string | null>(null);
  const uuid = sessionStorage.getItem('uuid') || localStorage.getItem('uuid');
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      const userMessage = addMessage(content, true);
      setMessages(prev => [...prev, userMessage]);

      const loadingMessage = addMessage('', false, true);
      setMessages(prev => [...prev, loadingMessage]);
      setIsLoading(true);

      try {
        let previousId = lastResponseIdRef.current;
        if (previousId === null) {
          for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (!msg.isUser && msg.responseId) {
              previousId = msg.responseId;
              break;
            }
          }
        }

        const body: AskAIRequestBody = {
          user_question: content,
          previous_response_id: previousId,
          email_id: email || uuid || ''
        };
        const response = await askAI(body);

        setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));
        const aiMessage = addMessage(
          response.response_text.response[0].response,
          false,
          false,
          response.response_text.response_id,
          response.response_text.sources
        );
        setMessages(prev => [...prev, aiMessage]);
        lastResponseIdRef.current = response.response_text.response_id;
      } catch (error) {
        setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));
        if (error instanceof RateLimitError) {
          throw error;
        }
        const errorMessage = addMessage(
          'Sorry, I encountered an error. Please try again.',
          false
        );
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [setMessages, email, messages, uuid]
  );

  return {
    isLoading,
    sendMessage
  };
};
